import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { formatPhoneNumberInternational, logger, validPhoneNumber } from 'shared/lib'
import { errorHandler } from 'shared/api'
import { RequestCancelAxios } from 'shared/api/lib'
import { PhoneApi } from 'entities/Phone'
import {
  type IParamsCreateNumbersLandline,
  type IResponseGetNumbersLookupError,
} from 'entities/Phone/api/types'
import { type Phone } from 'entities/Phone/model/Phone'
import { errorNumberLookup } from 'pages/settings/ui/EnterNumberCommon/lib/errorNumberLookup'

const ERROR_MESSAGE = 'The phone number you entered is incorrect. Please check it and try again.'
const SUCCESS_MESSAGE = 'The phone number you entered is correct.'

export type IEnterNumberStoreConfig = {
  cancelRequestLandline: RequestCancelAxios
  fetchNumbersLandline: (number: string, update?: boolean) => Promise<Phone | { error: string }>
}

export class EnterNumberStore {
  private _focusTrigger = ''
  private _phone = ''
  private _phoneDisabled = false
  private _phoneError = ''
  private _phoneSuccess = ''
  private _isPhoneInvalid = false
  private _loadingCheckPhone = false

  private _cancelRequestLookup = new RequestCancelAxios()

  constructor(private readonly _config: IEnterNumberStoreConfig) {
    makeAutoObservable(this)
  }

  get payload(): Pick<IParamsCreateNumbersLandline, 'phone' | 'landline_phone'> {
    const phone = validPhoneNumber(this._phone)
      ? formatPhoneNumberInternational(this._phone).replaceAll(' ', '')
      : ''

    return {
      phone: phone,
      landline_phone: phone,
    }
  }

  get phone() {
    return this._phone
  }

  get focusTrigger() {
    return this._focusTrigger
  }

  get phoneError() {
    return this._phoneError
  }

  get phoneSuccess() {
    return this._phoneSuccess
  }

  get nextDisabled() {
    return Boolean(
      !this._phone || this._phoneError || this._loadingCheckPhone || !this._phoneSuccess
    )
  }

  get nextLoading() {
    return this._loadingCheckPhone
  }

  get phoneInternational() {
    return formatPhoneNumberInternational(this._phone).replaceAll(' ', '')
  }

  get phoneDisabled() {
    return this._phoneDisabled
  }

  syncData = (phone: Phone) => {
    this._phone = phone.national_number
  }

  dispose = () => {
    this._cancelRequestLookup.reset()
  }

  setPhoneDisabled = (value: boolean) => {
    this._phoneDisabled = value
  }

  private _fetchNumbersLookup = async (number: string) => {
    try {
      this._cancelRequestLookup.init()
      this._config.cancelRequestLandline.init()

      runInAction(() => {
        this._loadingCheckPhone = true
      })

      const response = await PhoneApi.getNumbersLookup(
        {
          number: number,
        },
        {
          cancelToken: this._cancelRequestLookup.token,
        }
      )

      const error = errorNumberLookup(response.data)

      if (error) {
        this.setPhoneError(error)

        return
      }

      const data = await this._config.fetchNumbersLandline(number)

      if ('error' in data) {
        if (data.error === 'No such number') {
          runInAction(() => {
            this._phoneSuccess = SUCCESS_MESSAGE
          })
        }
      } else {
        this.setPhoneError('The phone number exists in your organization')
      }
    } catch (e) {
      logger.error(e)

      errorHandler<IResponseGetNumbersLookupError>(e as Error).then(({ type, error }) => {
        if (type === 'axios-error') {
          if (error.response?.data.error) {
            this.setPhoneError(error.response?.data.error || ERROR_MESSAGE)
          }
        }
      })
    } finally {
      runInAction(() => {
        this._loadingCheckPhone = false
      })
    }
  }

  setPhone = (phone: string, check = true) => {
    this._phone = phone

    if (!check) return
    if (this.phoneInternational.length < 12) {
      this.setPhoneError('')
      return
    }

    const isPhoneInvalid = this._validatePhone(this.phoneInternational)

    if (!isPhoneInvalid) {
      this._fetchNumbersLookup(this.phoneInternational)
    }
  }

  setFocusTrigger = (): void => {
    this._focusTrigger = nanoid()
  }

  setPhoneError = (value: string) => {
    this._phoneSuccess = ''
    this._phoneError = value
  }

  private _validatePhone = (number: string) => {
    this._isPhoneInvalid = !number || !validPhoneNumber(number)

    this.setPhoneError(this._isPhoneInvalid ? ERROR_MESSAGE : '')

    return this._isPhoneInvalid
  }
}
